<template>
  <div class="card mb-25 border-0 rounded-0 bg-white add-client-card">
    <div class="card-header">
      <h3 class="text-black fw-semibold">Modifier un client</h3>
    </div>
    <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30 letter-spacing">
      <Form ref="formulaire" @submit="editClient" :validation-schema="clientSchema">
        <div class="row">
          <div class="col-md-4 mb-3">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Nom du client <span class="text-danger">*</span>
              </label>
              <Field name="nomClient" type="text" class="form-control shadow-none fs-md-15 text-black"
                placeholder="Entrer le nom" />
              <ErrorMessage name="nomClient" class="text-danger" />
            </div>
          </div>
          <div class="col-md-4 mb-3">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Prénom du client <span class="text-danger">*</span>
              </label>
              <Field name="prenomClient" type="text" class="form-control shadow-none fs-md-15 text-black"
                placeholder="Entrer le prénom" />
              <ErrorMessage name="prenomClient" class="text-danger" />
            </div>
          </div>
          <div class="col-md-4 mb-3">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Téléphone <span class="text-danger">*</span>
              </label>
              <vue-tel-input placeholder="Entrer le numéro de téléphone" v-model="telephone1"
                defaultCountry="BJ" mode="international" @validate="validatePhone"
                class="shadow-none fs-md-15 text-black py-2">
              </vue-tel-input>
              <div v-if="telephoneErreur" class="text-danger">Veuillez entrer un numéro correct</div>
            </div>
          </div>

          <div class="col-md-12 mt-3">
            <div class="d-flex align-items-center">
              <button ref="submitButton" class="btn btn-success me-3" type="submit">
                Modifier le client
              </button>
              <router-link to="/clients/liste-clients" class="btn btn-danger">
                <i class="fa fa-trash-o lh-1 me-1 position-relative top-2"></i>
                <span class="position-relative"></span>Annuler
              </router-link>
            </div>
          </div>
        </div>
      </Form>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref } from 'vue';
import { Form, Field, ErrorMessage } from 'vee-validate';
import * as Yup from 'yup';
import ApiService from '@/services/ApiService';
import { error, success } from '@/utils/utils';
import { useRouter, useRoute } from 'vue-router';
import 'vue3-tel-input/dist/vue3-tel-input.css';
import { VueTelInput } from 'vue3-tel-input';

export default defineComponent({
  name: "EditClient",
  components: { Form, Field, ErrorMessage, VueTelInput },
  setup: () => {
    const router = useRouter();
    const route = useRoute();
    const clientId = Number(route.params.id);

    const formulaire = ref<any>(null);
    const submitButton = ref<HTMLButtonElement | null>(null);

    const clientSchema = Yup.object().shape({
      nomClient: Yup.string().required("Le nom du client est obligatoire"),
      prenomClient: Yup.string().required("Le prénom du client est obligatoire"),
    });

    // Téléphone : géré à part, car vue-tel-input fournit son propre contrôle de validité
    const telephone1 = ref('');
    const numeroValide = ref(false);
    const telephoneErreur = ref(false);

    const validatePhone = (phoneObject: any) => {
      numeroValide.value = !!phoneObject?.valid;
      telephoneErreur.value = !!telephone1.value && !numeroValide.value;
    };

    const getClient = async () => {
      try {
        const { data } = await ApiService.get(`/clients/${clientId}`);
        const client = data.data;
        formulaire.value?.setValues({
          nomClient: client.nomClient ?? '',
          prenomClient: client.prenomClient ?? '',
        });
        telephone1.value = client.telephone1 ?? '';
        // Le numéro enregistré est déjà valide : on ne bloque pas l'envoi avant que le composant le revérifie
        numeroValide.value = !!telephone1.value;
      } catch (err: any) {
        error(err?.response?.data?.message ?? "Le client n'a pas pu être chargé.");
      }
    };

    const editClient = async (values: any) => {
      if (!telephone1.value || !numeroValide.value) {
        telephoneErreur.value = true;
        return;
      }

      if (submitButton.value) submitButton.value.disabled = true;

      ApiService.put(`/clients/${clientId}`, {
        nomClient: values.nomClient,
        prenomClient: values.prenomClient,
        telephone1: telephone1.value,
      })
        .then(({ data }) => {
          if (data.code == 200) {
            success(data.message);
            router.push({ name: "ListeClient" });
          }
        })
        .catch((err) => {
          if (submitButton.value) submitButton.value.disabled = false;
          error(err?.response?.data?.message ?? "Le client n'a pas pu être modifié.");
        });
    };

    onMounted(() => {
      if (Number.isInteger(clientId)) {
        getClient();
      }
    });

    return {
      formulaire, submitButton, clientSchema, editClient,
      telephone1, telephoneErreur, validatePhone,
    };
  },
});
</script>